import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MachineService} from '@services/administration/machine/machine.service';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {Trigger} from '@models/adminstration/machine/trigger/trigger';
import {TriggerQueryAction} from '@models/adminstration/machine/trigger/trigger-query/trigger-query-action';
import {TriggerQuery} from '@models/adminstration/machine/trigger/trigger-query/trigger-query';
import {AddTriggerQueryRequest} from '@models/adminstration/machine/trigger/trigger-query/add-trigger-query-request';
import {TRIGGER_ACTION_TYPE} from '@constants/administration/machine/trigger-query/trigger-action-type';
import {UNSIGNED_INTEGER_NUMBERS_ONLY_REGEX} from '@constants/reg-exp';
import {
  UpdateTriggerQueryRequest
} from '@models/adminstration/machine/trigger/trigger-query/update-trigger-query-request';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';

@Component({
    selector: 'scitis-admin-trigger-query-edit',
    templateUrl: './trigger-query-edit.component.html',
    styleUrls: ['./trigger-query-edit.component.scss'],
    standalone: false
})
export class TriggerQueryEditComponent implements OnInit {

  @Input()
  triggerQuery: TriggerQuery = new TriggerQuery(-1);

  @Input()
  trigger: Trigger = new Trigger();

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  isMachineGroupTrigger: boolean = false;

  actionTypes: string[] = new Array<string>();
  selectedActionType: string = TRIGGER_ACTION_TYPE.EMAIL;
  actionTypeDropdownEnabled = true;

  triggerQueryAction: TriggerQueryAction = new TriggerQueryAction();
  triggerQueryEmailFormIsValid = false;
  triggerQueryNotificationFormIsValid = false;
  triggerQueryPubSubFormIsValid = false;
  triggerQueryForm: UntypedFormGroup = new UntypedFormGroup({});
  addTriggerAction = false;
  TRIGGER_ACTION_TYPE = TRIGGER_ACTION_TYPE;
  private EMAIL_SEPARATOR: string = ';';

  constructor(
      private activeModal: NgbActiveModal,
      private machineService: MachineService) {
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.triggerQueryForm.controls;
  }

  get isSubmitButtonDisabled(): boolean {
    return this.invalidForm();
  }

  ngOnInit(): void {
    if (this.trigger.id === -1) {
      this.addTriggerAction = true;
    }
    this.setActionTypes();
    this.generateTriggerQueryForm(this.trigger);
    this.setProperties();
    if ((this.userRole.isDistributorRole || this.userRole.isAnalyticsRole || this.userRole.isUserRole || this.userRole.isViewerRole) &&
        !(this.selectedActionType === TRIGGER_ACTION_TYPE.NOTIFICATION || this.selectedActionType === TRIGGER_ACTION_TYPE.EMAIL)) {
      this.triggerQueryForm.disable();
    }
  }

  onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onActionTypeChange(actionType: string): void {
    this.selectedActionType = actionType;

    switch (actionType) {
      case TRIGGER_ACTION_TYPE.EMAIL:
        this.triggerQueryNotificationFormIsValid = true;
        this.triggerQueryPubSubFormIsValid = true;
        break;
      case TRIGGER_ACTION_TYPE.NOTIFICATION:
        this.triggerQueryEmailFormIsValid = true;
        this.triggerQueryPubSubFormIsValid = true;
        break;
      case TRIGGER_ACTION_TYPE.PUBSUB:
        this.triggerQueryEmailFormIsValid = true;
        this.triggerQueryNotificationFormIsValid = true;
        break;
    }
  }

  onUpdatedTriggerQueryEmail(updatedTriggerQueryEmail: TriggerQueryAction) {
    const emails = this.getUniqueEmails(updatedTriggerQueryEmail);

    if (this.addTriggerAction) {
      this.triggerQuery.email = emails;
      this.triggerQuery.subject = updatedTriggerQueryEmail.subject;
      this.triggerQuery.message = updatedTriggerQueryEmail.message;
    } else {
      this.trigger.email = emails;
      this.trigger.subject = updatedTriggerQueryEmail.subject;
      this.trigger.message = updatedTriggerQueryEmail.message;
    }
  }

  onUpdatedTriggerNotification(updatedTriggerQueryEmail: TriggerQueryAction) {
    if (this.addTriggerAction) {
      this.triggerQuery.notificationSubscriptions = updatedTriggerQueryEmail.subscriberList;
      this.triggerQuery.subject = updatedTriggerQueryEmail.subject;
      this.triggerQuery.message = updatedTriggerQueryEmail.message;
    } else {
      this.trigger.notificationSubscriptions = updatedTriggerQueryEmail.subscriberList;
      this.trigger.subject = updatedTriggerQueryEmail.subject;
      this.trigger.message = updatedTriggerQueryEmail.message;
    }
  }

  onUpdatedTriggerPubSub(updatedTriggerQueryEmail: TriggerQueryAction): void {
    const {topic, message, messageContentType, additionalMessageFields} = updatedTriggerQueryEmail;

    this.triggerQuery.topic = this.trigger.topic = topic;
    this.triggerQuery.message = this.trigger.message = message;
    this.triggerQuery.messageContentType = this.trigger.messageContentType = messageContentType;
    this.triggerQuery.additionalMessageFields = this.trigger.additionalMessageFields = additionalMessageFields;
  }


  onTriggerQueryEmailFormIsValid(triggerQueryEmailFormIsValid: boolean) {
    this.triggerQueryEmailFormIsValid = triggerQueryEmailFormIsValid;
  }

  onTriggerQueryNotificationFormIsValid(triggerQueryNotificationFormIsValid: boolean) {
    this.triggerQueryNotificationFormIsValid = triggerQueryNotificationFormIsValid;
  }

  onTriggerQueryPubSubFormIsValid(triggerQueryPubSubFormIsValid: boolean) {
    this.triggerQueryPubSubFormIsValid = triggerQueryPubSubFormIsValid;
  }

  onSave(): void {
    if (this.invalidForm()) {
      return;
    }
    if (this.addTriggerAction) {
      this.setTriggerQueryProperties();
      const addTriggerQueryRequest = new AddTriggerQueryRequest(this.triggerQuery);
      this.activeModal.close({request: addTriggerQueryRequest, parentId: this.triggerQuery.parentId});
    } else {
      this.setTriggerProperties();
      const updateTriggerQueryRequest = new UpdateTriggerQueryRequest(this.trigger);
      this.activeModal.close({request: updateTriggerQueryRequest, parentId: this.trigger.parentId});
    }
  }

  private getUniqueEmails(updatedTriggerQueryEmail: TriggerQueryAction): string {
    let allEmails = '';
    if (!this.machineService.isNullOrEmptyOrWhiteSpace(updatedTriggerQueryEmail.email)) {
      allEmails = updatedTriggerQueryEmail.email;
    }
    if (updatedTriggerQueryEmail.subscriberList.length > 0) {
      if (allEmails.length > 0) {
        allEmails += ';';
      }
      allEmails += updatedTriggerQueryEmail.subscriberList.join(this.EMAIL_SEPARATOR);
    }

    const emailsArray: string[] = [];
    const emails = allEmails.split(this.EMAIL_SEPARATOR);
    if (emails.length > 0) {
      if (emails && emails.length > 0) {
        for (const email of emails) {
          if (!emailsArray.includes(email)) {
            emailsArray.push(email);
          }
        }
      }
    }
    return emailsArray.join(this.EMAIL_SEPARATOR);
  }

  private invalidForm(): boolean {
    return this.triggerQueryForm.invalid
        || !this.triggerQueryEmailFormIsValid
        || !this.triggerQueryNotificationFormIsValid
        || !this.triggerQueryPubSubFormIsValid;
  }

  private setActionTypes(): void {
    this.actionTypes.push(TRIGGER_ACTION_TYPE.EMAIL);
    if (this.isMachineGroupTrigger || this.userRole.isViewerRole || this.userRole.isUserRole || this.userRole.isAnalyticsRole) {
      this.actionTypeDropdownEnabled = false;
      return;
    }
    this.actionTypes.push(TRIGGER_ACTION_TYPE.NOTIFICATION);
    if (this.userRole.isDistributorRole) {
      return;
    }

    if (this.userRole.isAdminRole || this.userRole.isTechAdminRole) {
      return;
    }
    this.actionTypes.push(TRIGGER_ACTION_TYPE.PUBSUB);
  }

  private generateTriggerQueryForm(trigger: Trigger): void {
    if (trigger.id > 0) {
      this.onActionTypeChange(trigger.triggerActionType);
    } else {
      this.onActionTypeChange(TRIGGER_ACTION_TYPE.EMAIL);
    }
    this.triggerQueryForm = new UntypedFormGroup({
      actionType: new UntypedFormControl({
        value: this.selectedActionType,
        disabled: !this.actionTypeDropdownEnabled
      }, [Validators.required]),
      name: new UntypedFormControl(trigger.name, [Validators.required]),
      delayInSeconds: new UntypedFormControl(trigger.delayInSeconds,
          [Validators.pattern(UNSIGNED_INTEGER_NUMBERS_ONLY_REGEX), Validators.min(0), Validators.max(2147483647)])
    });
  }

  private setTriggerQueryProperties(): void {
    this.triggerQuery.triggerActionType = this.triggerQueryForm.controls.actionType.value;
    this.triggerQuery.name = this.triggerQueryForm.controls.name.value;
    this.triggerQuery.delayInSeconds = this.triggerQueryForm.controls.delayInSeconds.value;
    this.triggerQuery.isParentMachine = !this.isMachineGroupTrigger;

    if (this.triggerQuery.triggerActionType === TRIGGER_ACTION_TYPE.NOTIFICATION && this.triggerQuery.email) {
      this.triggerQuery.notificationSubscriptions = this.triggerQuery.email.split(this.EMAIL_SEPARATOR);
    }
  }

  private setTriggerProperties(): void {
    this.trigger.triggerActionType = this.triggerQueryForm.controls.actionType.value;
    this.trigger.name = this.triggerQueryForm.controls.name.value;
    this.trigger.delayInSeconds = this.triggerQueryForm.controls.delayInSeconds.value;
    this.trigger.isParentMachine = !this.isMachineGroupTrigger;
  }

  private setProperties() {
    switch (this.trigger.triggerActionType) {
      case TRIGGER_ACTION_TYPE.EMAIL:
        this.setEmailActionTypeProperties();
        break;
      case TRIGGER_ACTION_TYPE.NOTIFICATION:
        this.setNotificationActionTypeProperties();
        break;
      case TRIGGER_ACTION_TYPE.PUBSUB:
        this.setPubSubActionTypeProperties();
        break;

    }
  }

  private setEmailActionTypeProperties() {
    this.triggerQueryAction.email = this.trigger.email;
    if (this.trigger.notificationSubscriptions) {
      this.triggerQueryAction.subscriberList = this.trigger.notificationSubscriptions;
    }
    this.triggerQueryAction.subject = this.trigger.subject;
    this.triggerQueryAction.message = this.trigger.message;
  }

  private setNotificationActionTypeProperties(): void {
    if (this.trigger.notificationSubscriptions) {
      this.triggerQueryAction.subscriberList = this.trigger.notificationSubscriptions;
    }
    if (this.trigger.email && this.trigger.email !== '') {
      const emails = this.trigger.email.split(this.EMAIL_SEPARATOR);
      this.triggerQueryAction.subscriberList = [...this.triggerQueryAction.subscriberList.concat(emails)];
    }
    this.triggerQueryAction.subject = this.trigger.subject;
    this.triggerQueryAction.message = this.trigger.message;
  }

  private setPubSubActionTypeProperties(): void {
    this.triggerQueryAction.topic = this.trigger.topic;
    this.triggerQueryAction.message = this.trigger.message;
    this.triggerQueryAction.messageContentType = this.trigger.messageContentType;
    this.triggerQueryAction.additionalMessageFields = this.trigger.additionalMessageFields;
  }
}
